// Leads list + lead detail / transcript / activity screens ka shared code:
// status labels, time formatting, aur detail fetch ka hook.
import { useCallback, useEffect, useRef, useState } from "react";
import { getPounceLeadDetail } from "../../Store/apiStore";
import { getNotificationSocket } from "../../utils/initNotificationSocket";
import useAgentPermissions from "../../hooks/useAgentPermissions";
import { getAgentOwnerId } from "../../lib/activeContext";

/**
 * Pounce screens ki permissions (canEditAgents / canViewCallLogs /
 * canViewAppointments) — usi owner ke hisaab se jiska data API deti hai
 * (apiStore Pounce calls pe yahi owner X-Owner-Id me bhejta hai). Owner khud aur
 * team admin: sab true. Server har route pe khud bhi jaanchta hai; ye sirf wo
 * options chhupane ke liye hai jo chalenge hi nahi.
 */
export const usePouncePermissions = () => useAgentPermissions(getAgentOwnerId() || "");

// Socket na pahunche (server restart, kai server) to bhi screen itni der me taaza.
const FALLBACK_REFRESH_MS = 60000;
// Ek call pe kuch hi second me kai events aate hain (khatam, analysis, SMS) — ek fetch.
const EVENT_DEBOUNCE_MS = 400;

// `tone` = PounceLeadCard.module.css ki status class (detail screens bhi wahi use karti hain).
export const STATUS = {
  new: { label: "New", tone: "statusNeutral" },
  calling: { label: "Calling", tone: "statusInfo" },
  booked: { label: "Booked", tone: "statusSuccess" },
  qualified_no_book: { label: "Hot lead", tone: "statusBrand" },
  not_interested: { label: "Not interested", tone: "statusNeutral" },
  no_answer: { label: "No answer", tone: "statusWarning" },
  bad_number: { label: "Bad number", tone: "statusDanger" },
  opted_out: { label: "Opted out", tone: "statusDanger" },
  blocked: { label: "Blocked", tone: "statusDanger" },
  failed: { label: "Failed", tone: "statusDanger" },
};

// Pounce off ke dauraan ruki lead — backend pounceService.HOLD_REASON se match.
export const HOLD_REASON = "Paused — Outbound Caller is off";
export const isHeld = (lead) => ["new", "calling"].includes(lead?.status) && lead?.closedReason === HOLD_REASON;

// Lead-deal plan ki monthly limit poori — lead agle check ka intezaar kar rahi
// hai. Backend pounceService.LIMIT_WAIT_REASON se match.
export const LIMIT_WAIT_REASON = "Waiting — monthly lead limit reached";
export const isLimitWaiting = (lead) =>
  ["new", "calling"].includes(lead?.status) && lead?.closedReason === LIMIT_WAIT_REASON;

// `calling` me do haalat hain: call abhi lag rahi hai, ya pichli call ke baad agla
// retry khada hai (nextAttemptAt aage ka). Doosri haalat "Calling" nahi hai.
const retryLabel = (lead) => {
  if (lead?.status !== "calling" || !lead.nextAttemptAt) return null;
  const mins = Math.ceil((new Date(lead.nextAttemptAt).getTime() - Date.now()) / 60000);
  if (!(mins > 0)) return null; // due ho chuka — cron abhi uthayega
  return mins < 60 ? `Retry in ${mins}m` : `Retry ${formatWhen(lead.nextAttemptAt, lead.displayTimezone)}`;
};

export const statusOf = (lead) => {
  if (isHeld(lead)) return { label: "On hold", tone: "statusWarning" };
  if (isLimitWaiting(lead)) return { label: "Waiting", tone: "statusWarning" };
  const retry = retryLabel(lead);
  if (retry) return { label: retry, tone: "statusWarning" };
  return STATUS[lead?.status] || { label: lead?.status || "", tone: "statusNeutral" };
};

// Ek call attempt ka status → insaani label.
export const ATTEMPT_LABEL = {
  completed: "Completed",
  no_answer: "No answer",
  busy: "Line busy",
  failed: "Failed",
  in_progress: "In progress",
  dialing: "Dialing",
  pending: "Pending",
  skipped: "Not dialed",
  cancelled: "Cancelled",
};

// Attempt ka label: pehle reasonCode (call judi hi nahi / galat number), phir status.
// `not_connected` = line/carrier ki galti, attempt gina nahi gaya — wahi number dobara dial hota hai.
export const attemptLabel = (a, { note = false } = {}) => {
  if (a?.reasonCode === "not_connected") return note ? "Not connected (not counted)" : "Not connected";
  if (a?.reasonCode === "invalid_destination") return "Wrong number";
  return ATTEMPT_LABEL[a?.status] || a?.status;
};

const SOURCE_LABELS = { manual: "Added manually", webhook: "Webhook" };
export const sourceLabel = (source) => (source ? SOURCE_LABELS[source] || source : "");

export const initials = (text) =>
  String(text || "")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();

// Lead ke time business ke timezone me (server ka `displayTimezone` = Pounce agent
// ka timezone, call history jaisa). `timeZone` na ho to device ka. DB me galat
// naam ho to toLocaleString RangeError phenkta hai — tab bhi device ka.
const inZone = (date, options, timeZone) => {
  const d = new Date(date);
  try {
    return d.toLocaleString("en-US", timeZone ? { ...options, timeZone } : options);
  } catch {
    return d.toLocaleString("en-US", options);
  }
};

export const formatWhen = (iso, timeZone) =>
  iso ? inZone(iso, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }, timeZone) : "";

export const formatFull = (iso, timeZone) =>
  iso
    ? inZone(iso, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }, timeZone)
    : "";

export const formatClock = (date, timeZone) =>
  date ? inZone(date, { hour: "numeric", minute: "2-digit" }, timeZone) : "";

// 84 → "1m 24s", 42 → "42s"
export const formatDuration = (sec) => {
  const s = Math.round(Number(sec) || 0);
  if (!s) return "";
  const m = Math.floor(s / 60);
  return m ? `${m}m ${s % 60}s` : `${s}s`;
};

// Sirf wo attempts jinpe sach me call lagi (Retell ne call_id diya), naya pehle.
export const dialedAttempts = (lead) =>
  (lead?.attempts || [])
    .filter((a) => a.retellCallId || (a.status && a.status !== "skipped"))
    // Not-connected redial ka attemptNo wahi rehta hai — id se order pakka rakho.
    .sort((a, b) => b.attemptNo - a.attemptNo || b.id - a.id);

// Lead aane se pehli call tak ke second. Waqt na mile, ya ek ghante se zyada ho, to null.
export const firstContactSeconds = (lead) => {
  const first = dialedAttempts(lead).slice(-1)[0];
  const startedAt = first?.startedAt || first?.createdAt;
  if (!startedAt || !lead?.createdAt) return null;
  const secs = (new Date(startedAt) - new Date(lead.createdAt)) / 1000;
  return secs > 0 && secs < 3600 ? secs : null;
};

// "EDT" jaisa chhota naam; na bane to null.
const tzShort = (timeZone, date) => {
  if (!timeZone) return null;
  try {
    return new Intl.DateTimeFormat("en-US", { timeZone, timeZoneName: "short" })
      .formatToParts(date)
      .find((p) => p.type === "timeZoneName")?.value;
  } catch {
    return null;
  }
};

/**
 * Appointment ka date + time label. date/time appointment ke apne timezone ka
 * "deewar ki ghadi" waqt hai, isliye bina convert kiye wahi dikhate hain.
 * { date: "Tue, Mar 11, 2025", time: "2:00 PM – 2:30 PM EDT" }
 */
export const appointmentLabel = (appt) => {
  if (!appt?.date) return { date: "", time: "" };
  const start = new Date(`${appt.date}T${String(appt.time || "00:00").slice(0, 5)}:00`);
  if (Number.isNaN(start.getTime())) return { date: appt.date, time: appt.time || "" };
  const end = new Date(start.getTime() + (Number(appt.durationMins) || 30) * 60000);
  const tz = tzShort(appt.timezone, start);
  return {
    date: start.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" }),
    time: `${formatClock(start)} – ${formatClock(end)}${tz ? ` ${tz}` : ""}`,
  };
};

// Retell analysis → qualification ke rows. Khaali value wali row nahi banti.
const yesNo = (v) => (v === true || v === "true" ? "Yes" : v === false || v === "false" ? "No" : null);
// Khaali / "Unknown" → null
const known = (v) => (v && String(v).trim() && String(v).trim() !== "Unknown" ? v : null);
export const qualificationRows = (analysis) => {
  if (!analysis) return [];
  return [
    ["Service needed", analysis.service_requested],
    // Timeline: project_start_date ho to wahi, warna project_timeline
    ["Timeline", known(analysis.project_start_date) || known(analysis.project_timeline)],
    ["Decision maker", yesNo(analysis.is_decision_maker)],
    ["Qualified", yesNo(analysis.is_qualified)],
    ["Sentiment", analysis.user_sentiment],
  ]
    .filter(([, value]) => value !== null && value !== undefined && String(value).trim() !== "")
    .map(([label, value]) => ({ label, value: String(value) }));
};

/**
 * Lead badalne pe `onChange()` — server ka socket event "pounce_lead_updated".
 * Saath me: socket reconnect aur app wapas khulne pe bhi (offline ke beech ke
 * events chhoot jaate hain), aur 60s ka fallback. `leadId` diya ho to sirf usi
 * lead ke events.
 */
export const usePounceLeadEvents = (onChange, leadId) => {
  const cb = useRef(onChange);
  cb.current = onChange;
  const timer = useRef(null);
  const [socketTick, setSocketTick] = useState(0);

  // Event chahe jitne aayein, ek hi refetch.
  const fire = useCallback(() => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => cb.current(), EVENT_DEBOUNCE_MS);
  }, []);

  useEffect(() => {
    const socket = getNotificationSocket();
    // App.js socket baad me banata hai — milne tak har second dobara dekho.
    if (!socket) {
      const t = setTimeout(() => setSocketTick((n) => n + 1), 1000);
      return () => clearTimeout(t);
    }
    const onUpdate = (p) => (!leadId || String(p?.leadId) === String(leadId)) && fire();
    socket.on("pounce_lead_updated", onUpdate);
    socket.on("connect", fire);
    return () => {
      socket.off("pounce_lead_updated", onUpdate);
      socket.off("connect", fire);
    };
  }, [leadId, fire, socketTick]);

  useEffect(() => {
    const onVisible = () => !document.hidden && fire();
    document.addEventListener("visibilitychange", onVisible);
    const id = setInterval(() => !document.hidden && cb.current(), FALLBACK_REFRESH_MS);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      clearInterval(id);
      clearTimeout(timer.current);
    };
  }, [fire]);
};

/** Lead detail fetch, live. attemptId optional (transcript screen me doosri call ke liye). */
export const useLeadDetail = (leadId, attemptId) => {
  const [lead, setLead] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(() => {
    setError("");
    return getPounceLeadDetail(leadId, attemptId)
      .then(setLead)
      .catch((err) =>
        setError(
          {
            404: "This lead wasn't found.",
            // Team member ke paas call logs ki permission nahi.
            403: "You don't have access to this lead. Ask the account owner for call log access.",
          }[err?.response?.status] || "We couldn't load this lead."
        )
      );
  }, [leadId, attemptId]);

  useEffect(() => {
    load();
  }, [load]);

  usePounceLeadEvents(load, leadId);

  return { lead, error, reload: load };
};
